import Redis, { RedisOptions } from 'ioredis'
import { logger } from '@mc/pino-logger'

/**
 * Server-only Redis client for the jarvis-vision BFF, used to coordinate
 * refresh-token exchanges across the app's many pods (see `tokenRefresh.ts`).
 *
 * Connection comes from `REDIS_URL`, which points at the shared `persona-redis`
 * ElastiCache cluster (same VPC, `noeviction`, TLS via `rediss://`). This is a
 * deliberate choice over `@mc/redis`'s `PRE_PAYWALL_REDIS_*` (the prepaywall LRU
 * cluster): `noeviction` suits lock semantics, and those creds aren't present in
 * the `jarvis-vision-web` namespace anyway. Our keys are namespaced
 * (`educator:token:refresh:*`) so they never collide with persona's data.
 *
 * REQUIRES: `REDIS_URL` must be present in the `staging/` and
 * `production/jarvis-vision-web` AWS Secrets Manager secrets (the ExternalSecret
 * extracts every key into the pod env). If it is absent, this returns `null` and
 * cross-pod coordination is disabled — callers fall back to a direct exchange
 * (the pre-coordination behaviour), so a missing/var or Redis outage degrades
 * gracefully rather than becoming an auth outage.
 *
 * This client is deliberately *fail-fast and fail-open*: short timeouts and no
 * offline queue mean that when Redis is unreachable, commands reject immediately
 * rather than hanging, and callers fall back to the in-process behaviour.
 */

// Never construct a client in the browser — ioredis is a Node-only module and
// these credentials must never reach client bundles. Mirrors `@mc/redis`.
const isServer = typeof window === 'undefined'

const { REDIS_URL } = process.env

const getRedisClient = (): Redis | null => {
  if (!isServer) {
    return null
  }

  if (!REDIS_URL) {
    logger.warn({
      msg: 'REDIS_URL is not set — token-refresh coordination is disabled, so concurrent refreshes of the same refresh token will race',
    })
    return null
  }

  // ElastiCache in-transit encryption uses the `rediss://` scheme. ioredis
  // enables TLS automatically from the scheme; we relax cert verification to
  // match the established `@mc/redis` configuration for the same clusters.
  const useTls = REDIS_URL.startsWith('rediss://')
  // Next dev compiles stall the event loop past 500ms, which would trip the
  // fail-fast timeouts and drop refreshes out of coordination.
  const isDevelopment = process.env.NODE_ENV === 'development'

  const options: RedisOptions = {
    // Fail fast: a slow/broken Redis must not delay the auth path. Callers
    // treat any rejection as "Redis unavailable" and fall back.
    connectTimeout: isDevelopment ? 5000 : 500,
    commandTimeout: isDevelopment ? 3000 : 500,
    maxRetriesPerRequest: 1,
    // Reject commands immediately while disconnected instead of buffering them
    // — buffered auth commands would resolve late and unpredictably.
    enableOfflineQueue: isDevelopment,
    retryStrategy: (times) => Math.min(times * 100, 3000),
    ...(useTls ? { tls: { rejectUnauthorized: false } } : {}),
  }

  try {
    const client = new Redis(REDIS_URL, options)
    client.on('error', (error) => {
      // ioredis attaches the failed command (AUTH included) to connection
      // errors, so never hand the whole object to the serializer here.
      logger.error({
        msg: 'Redis client error',
        err: { type: error.name, message: error.message },
      })
    })
    return client
  } catch (error) {
    logger.error({ msg: 'Failed to initialize Redis client', err: error })
    return null
  }
}

export const redisClient = getRedisClient()
